import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Featured image: carousel with actions, or the upload / generating placeholder.
 * Extracted verbatim from pages/Articles/Article.js.
 */
export default function ArticleThumbnailPanel({
  currentThumbnailIndex,
  handleSetAsThumbnail,
  handleThumbnailDelete,
  handleThumbnailDownload,
  handleThumbnailDragLeave,
  handleThumbnailDragOver,
  handleThumbnailDrop,
  handleThumbnailFileSelect,
  handleThumbnailNext,
  handleThumbnailPrevious,
  handleThumbnailRegenerate,
  handleThumbnailSelect,
  isDraggingThumbnail,
  isThumbnailGenerating,
  isUploadingThumbnail,
  setShowGalleryModal,
  thumbnailArray,
  thumbnailData,
  title,
}) {
  const { t } = useTranslation();

  return (
    <>
      {thumbnailData && thumbnailData.url ? (
        <div className="bg-white rounded-lg shadow-sm border mb-6 overflow-hidden">
          <div className="relative group">
            {/* 4:3 aspect ratio container */}
            <div className="relative w-full" style={{ paddingTop: '75%' }}>
              <img
                src={thumbnailData.url}
                alt={title}
                className="absolute top-0 left-0 w-full h-full object-cover"
              />
            </div>

            {/* Navigation arrows for carousel */}
            {thumbnailArray.length > 1 && (
              <>
                <button
                  onClick={handleThumbnailPrevious}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-2 rounded-full shadow-lg transition-all hover:scale-105 opacity-0 group-hover:opacity-100"
                  title={t('articles.editor.thumbnail.previous')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={handleThumbnailNext}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-2 rounded-full shadow-lg transition-all hover:scale-105 opacity-0 group-hover:opacity-100"
                  title={t('articles.editor.thumbnail.next')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}

            {/* Thumbnail counter and gallery button */}
            {thumbnailArray.length > 1 && (
              <>
                <div className="absolute top-4 left-4 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                  {currentThumbnailIndex + 1} / {thumbnailArray.length}
                </div>
                <button
                  onClick={() => setShowGalleryModal(true)}
                  className="absolute bottom-4 left-4 bg-black bg-opacity-80 hover:bg-opacity-90 text-white px-3 py-2 rounded-lg shadow-lg transition-all hover:scale-105 opacity-0 group-hover:opacity-100 flex items-center gap-2 text-sm font-medium"
                  title={t('articles.editor.thumbnail.viewAll')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {t('articles.editor.thumbnail.viewGallery')}
                </button>
              </>
            )}

            {/* Action buttons */}
            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
              {/* SET button - only show if multiple thumbnails and current is not chosen */}
              {thumbnailArray.length > 1 && !thumbnailArray[currentThumbnailIndex]?.chosen && (
                <button
                  onClick={handleSetAsThumbnail}
                  className="btn btn-secondary btn-sm shadow-lg hover:scale-105"
                  title={t('articles.editor.thumbnail.setAsThumbnail')}
                >
                  SET
                </button>
              )}
              <button
                onClick={handleThumbnailRegenerate}
                className="btn bg-black bg-opacity-80 hover:bg-opacity-90 text-white p-2.5 rounded-lg shadow-lg hover:scale-105"
                title={t('articles.editor.thumbnail.generateFromPexels')}
                disabled={isUploadingThumbnail}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button
                onClick={handleThumbnailDownload}
                className="btn bg-black bg-opacity-80 hover:bg-opacity-90 text-white p-2.5 rounded-lg shadow-lg hover:scale-105"
                title={t('articles.editor.thumbnail.download')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
              <button
                onClick={handleThumbnailDelete}
                className="btn bg-black bg-opacity-80 hover:bg-opacity-90 text-white p-2.5 rounded-lg shadow-lg hover:scale-105"
                title={t('articles.editor.thumbnail.delete')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>

            {/* Loading overlay */}
            {isUploadingThumbnail && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                <div className="flex flex-col items-center text-white">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-2"></div>
                  <p className="text-sm">Regenerating...</p>
                </div>
              </div>
            )}
          </div>

          {/* Thumbnail carousel dots */}
          {thumbnailArray.length > 1 && (
            <div className="flex justify-center gap-2 p-4 bg-gray-50">
              {thumbnailArray.map((thumb, index) => (
                <button
                  key={index}
                  onClick={() => handleThumbnailSelect(index)}
                  className={`w-3 h-3 rounded-full transition-all relative ${
                    index === currentThumbnailIndex
                      ? 'bg-primary-600 scale-125'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  title={`Thumbnail ${index + 1}${thumb.photographer ? ` by ${thumb.photographer}` : ''}${thumb.chosen ? ' (MAIN)' : ''}`}
                >
                  {/* Main thumbnail indicator */}
                  {thumb.chosen && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full border border-white"></div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div
          className={`bg-white rounded-lg shadow-sm border-2 border-dashed mb-6 p-12 text-center transition-colors ${
            isDraggingThumbnail
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={!isThumbnailGenerating ? handleThumbnailDrop : undefined}
          onDragOver={!isThumbnailGenerating ? handleThumbnailDragOver : undefined}
          onDragLeave={!isThumbnailGenerating ? handleThumbnailDragLeave : undefined}
        >
          {isUploadingThumbnail || isThumbnailGenerating ? (
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
              <p className="text-gray-600 font-medium mb-2">
                {isUploadingThumbnail ? 'Uploading thumbnail...' : 'Generating thumbnail...'}
              </p>
              {isThumbnailGenerating && (
                <p className="text-sm text-gray-500">
                  Your AI-generated thumbnail will appear here shortly
                </p>
              )}
            </div>
          ) : (
            <>
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Add Featured Image
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Drag and drop an image here, or click to select
              </p>
              <div className="flex items-center justify-center gap-3">
                <label className="cursor-pointer">
                  <span className="btn btn-primary btn-md">
                    Select Image
                  </span>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleThumbnailFileSelect}
                  />
                </label>
                <button
                  onClick={handleThumbnailRegenerate}
                  className="btn btn-primary-outline btn-md gap-2 rounded-full"
                  disabled={isUploadingThumbnail}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Find on Pexels
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
